import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useDisputedOrders, fetchOrderDelivery } from '../../hooks/useAdminData';
import { stripeApi } from '../../firebase/functionsService';
import { massLabel } from '../../utils/massCatalog';
import { orderHourlyText, orderQtyText } from '../../utils/orderHourly';
import { COLORS } from '../../styles/tokens';
import { useIsMobile } from '../../hooks/useIsMobile';

export default function DisputeManager() {
  const { orders, loading } = useDisputedOrders();
  const [selected, setSelected] = useState(null);
  const [delivery, setDelivery] = useState(null);
  const [loadingDelivery, setLoadingDelivery] = useState(false);
  const isMobile = useIsMobile();

  async function openDispute(order) {
    setSelected(order);
    setDelivery(null);
    setLoadingDelivery(true);
    const d = await fetchOrderDelivery(order.id);
    setDelivery(d);
    setLoadingDelivery(false);
  }

  if (loading) return <p style={{ color: COLORS.textLight }}>Laster tvister…</p>;

  return (
    <div style={styles.layout}>
      {/* Venstre: tvisteliste */}
      {/* På mobil vises liste og detaljer hver for seg */}
      <div style={{ ...styles.list, ...(isMobile ? { width: '100%', borderRight: 'none', display: selected ? 'none' : 'block' } : {}) }}>
        <h2 style={styles.listTitle}>Tvister ({orders.length})</h2>
        {orders.length === 0 && (
          <div style={styles.empty}>
            <div style={{ fontSize: 36 }}>✅</div>
            <p style={{ marginTop: 12, color: COLORS.textMid }}>Ingen aktive tvister</p>
          </div>
        )}
        {orders.map(order => (
          <div
            key={order.id}
            onClick={() => openDispute(order)}
            style={{
              ...styles.disputeCard,
              borderLeft: selected?.id === order.id ? `4px solid ${COLORS.danger}` : '4px solid transparent',
            }}
          >
            <div style={styles.cardTop}>
              <span style={styles.massTag}>{massLabel(order.massType)}</span>
              <span style={styles.disputeBadge}>TVIST</span>
            </div>
            <div style={{ fontSize: 13, color: COLORS.textMid, marginTop: 6 }}>
              {order.pickupAddress?.street} → {order.deliveryAddress?.street}
            </div>
            <div style={{ fontSize: 12, color: COLORS.textLight, marginTop: 4 }}>
              #{order.id.slice(-6)}{orderQtyText(order) && ` · ${orderQtyText(order)}`}
            </div>
          </div>
        ))}
      </div>

      {/* Høyre: tvistedetaljer */}
      <div style={{ ...styles.detail, display: isMobile && !selected ? 'none' : 'block' }}>
        {isMobile && selected && (
          <button onClick={() => setSelected(null)} style={styles.backBtn}>← Tilbake til listen</button>
        )}
        {!selected && (
          <div style={styles.emptyDetail}>
            <div style={{ fontSize: 48 }}>⚖️</div>
            <p style={{ marginTop: 16, color: COLORS.textLight }}>Velg en tvist for å behandle den</p>
          </div>
        )}
        {selected && (
          <DisputeDetail
            order={selected}
            delivery={delivery}
            loadingDelivery={loadingDelivery}
            onResolved={() => setSelected(null)}
          />
        )}
      </div>
    </div>
  );
}

function DisputeDetail({ order, delivery, loadingDelivery, onResolved }) {
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [verdict, setVerdict] = useState(null);
  const isMobile = useIsMobile();

  async function handleApprove() {
    setLoading(true);
    setError('');
    try {
      await updateDoc(doc(db, 'orders', order.id), {
        status:         'completed',
        disputeVerdict: 'approved',
        disputeResolvedAt: new Date(),
      });
      setVerdict('approved');
      setTimeout(onResolved, 1500);
    } catch {
      setError('Kunne ikke godkjenne. Prøv igjen.');
    } finally {
      setLoading(false);
    }
  }

  async function handleRefund() {
    setLoading(true);
    setError('');
    try {
      // Kall Cloud Function for Stripe refund
      await stripeApi.refundOrder(order.id);
      await updateDoc(doc(db, 'orders', order.id), {
        status:         'refunded',
        disputeVerdict: 'refunded',
        disputeResolvedAt: new Date(),
      });
      setVerdict('refunded');
      setTimeout(onResolved, 1500);
    } catch {
      setError('Stripe-refund feilet. Sjekk Cloud Functions-logger og prøv manuelt i Stripe Dashboard.');
      setLoading(false);
    }
  }

  if (verdict) {
    return (
      <div style={styles.verdictBox}>
        <div style={{ fontSize: 48 }}>{verdict === 'approved' ? '✅' : '💸'}</div>
        <p style={{ fontWeight: 700, fontSize: 18, marginTop: 12 }}>
          {verdict === 'approved' ? 'Levering godkjent' : 'Bestiller refundert'}
        </p>
      </div>
    );
  }

  return (
    <div style={{ ...styles.detailContent, ...(isMobile ? { padding: 16 } : {}) }}>
      <h2 style={styles.detailTitle}>
        Tvist #{order.id.slice(-6)} — {massLabel(order.massType)}
      </h2>

      <Section title="Oppdragsdetaljer">
        <Grid>
          <Field label="Henting"     value={order.pickupAddress?.street} />
          <Field label="Levering"    value={order.deliveryAddress?.street} />
          <Field label="Bestiller"   value={order.ordererUid?.slice(0, 8) + '…'} />
          <Field label="Sjåfør"      value={order.driverUid?.slice(0, 8) + '…'} />
          <Field label="Estimert"    value={orderQtyText(order)} />
          {orderHourlyText(order) && <Field label="Timer" value={orderHourlyText(order)} />}
          <Field label="Faktisk"     value={order.pickupActualTons ? `${order.pickupActualTons} tonn` : '—'} />
          {order.fixedPrice && <Field label="Pris" value={`kr ${order.fixedPrice},-`} />}
        </Grid>
      </Section>

      {order.pendingAdjustment && (
        <Section title="Vektavvik">
          <div style={styles.deviationBox}>
            Avvik: <strong>{order.pendingAdjustment.deviationPct}%</strong>
            {' · '}Prisjustering: <strong>kr {order.pendingAdjustment.priceAdjustment},-</strong>
            {' · '}Godkjent av bestiller: <strong>{order.pendingAdjustment.approvedByOrderer ? 'Ja' : 'Nei'}</strong>
          </div>
        </Section>
      )}

      {/* Leveringsbilder */}
      {loadingDelivery && <p style={{ color: COLORS.textLight, fontSize: 14 }}>Laster dokumentasjon…</p>}

      {delivery && (
        <>
          {delivery.pickupPhotoUrls?.length > 0 && (
            <Section title="Hentingsbilder">
              <PhotoGrid urls={delivery.pickupPhotoUrls} />
            </Section>
          )}
          {delivery.deliveryPhotoUrls?.length > 0 && (
            <Section title="Leveringsbilder">
              <PhotoGrid urls={delivery.deliveryPhotoUrls} />
            </Section>
          )}
          {delivery.receiverSignatureUrl && (
            <Section title="Mottakers signatur">
              <img
                src={delivery.receiverSignatureUrl}
                alt="Signatur"
                style={{ maxWidth: 280, border: `1px solid ${COLORS.border}`, borderRadius: 6, background: '#ffffff' }}
              />
            </Section>
          )}
          {delivery.weightDocumentUrl && (
            <Section title="Vektseddel">
              <img
                src={delivery.weightDocumentUrl}
                alt="Vektseddel"
                style={{ maxWidth: 200, border: `1px solid ${COLORS.border}`, borderRadius: 6 }}
              />
            </Section>
          )}
        </>
      )}

      {error && <p style={styles.error}>{error}</p>}

      <div style={styles.actions}>
        <button style={styles.refundBtn} onClick={handleRefund} disabled={loading}>
          💸 Refunder bestiller
        </button>
        <button style={styles.approveBtn} onClick={handleApprove} disabled={loading}>
          ✅ Godkjenn levering
        </button>
      </div>
    </div>
  );
}

// ── Hjelpere ─────────────────────────────────────────────────────────────────

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <h3 style={{ fontSize: 13, fontWeight: 700, color: COLORS.textLight, textTransform: 'uppercase', margin: '0 0 10px' }}>{title}</h3>
      {children}
    </div>
  );
}

function Grid({ children }) {
  const isMobile = useIsMobile();
  return <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '8px 20px' }}>{children}</div>;
}

function Field({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: COLORS.textLight, fontWeight: 600, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 14, color: COLORS.textDark }}>{value ?? '—'}</div>
    </div>
  );
}

function PhotoGrid({ urls }) {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {urls.map((url, i) => (
        <a key={i} href={url} target="_blank" rel="noreferrer">
          <img src={url} alt={`Bilde ${i + 1}`} style={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 6 }} />
        </a>
      ))}
    </div>
  );
}

const styles = {
  layout:       { display: 'flex', gap: 0, height: '100%' },
  list:         { width: 300, borderRight: `1px solid ${COLORS.border}`, padding: '0 0 20px', overflowY: 'auto', flexShrink: 0 },
  listTitle:    { fontSize: 15, fontWeight: 700, color: COLORS.textMid, padding: '16px 20px 12px', margin: 0, borderBottom: `1px solid ${COLORS.border}` },
  disputeCard:  { padding: '14px 20px', cursor: 'pointer', borderBottom: `1px solid ${COLORS.border}`, background: COLORS.white, transition: 'background 0.1s' },
  cardTop:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  massTag:      { fontSize: 12, fontWeight: 600, background: COLORS.surfaceHover, padding: '2px 8px', borderRadius: 4, color: COLORS.textDark },
  disputeBadge: { fontSize: 10, fontWeight: 700, background: COLORS.dangerBg, color: COLORS.dangerText, padding: '2px 8px', borderRadius: 4 },
  empty:        { textAlign: 'center', padding: '40px 20px' },
  emptyDetail:  { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: COLORS.textLight },
  detail:       { flex: 1, overflowY: 'auto' },
  backBtn:      { background: 'none', border: 'none', color: COLORS.blue, cursor: 'pointer', fontSize: 14, padding: '14px 16px 0' },
  detailContent:{ padding: '24px 28px' },
  detailTitle:  { fontSize: 18, fontWeight: 700, color: COLORS.textDark, margin: '0 0 20px' },
  deviationBox: { background: COLORS.warningBg, border: `1px solid ${COLORS.warningBorder}`, borderRadius: 6, padding: '10px 14px', fontSize: 13 },
  actions:      { display: 'flex', gap: 12, marginTop: 24 },
  approveBtn:   { flex: 1, padding: '12px', borderRadius: 8, border: 'none', background: COLORS.success, color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer' },
  refundBtn:    { flex: 1, padding: '12px', borderRadius: 8, border: `2px solid ${COLORS.danger}`, background: COLORS.white, color: COLORS.danger, fontWeight: 700, fontSize: 14, cursor: 'pointer' },
  error:        { background: COLORS.dangerBg, color: COLORS.dangerText, padding: '10px 12px', borderRadius: 6, fontSize: 13, marginTop: 12 },
  verdictBox:   { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: 40 },
};
